import CoffeeIcon from "@repo/ui-system/icons/random/coffee/outline.svg";
import GlobeIcon from "@repo/ui-system/icons/random/globe/outline.svg";
import HeadphonesIcon from "@repo/ui-system/icons/random/headphones/outline.svg";
import ListIcon from "@repo/ui-system/icons/random/list/outline.svg";
import MixerIcon from "@repo/ui-system/icons/random/mixer/outline.svg";
import MobileIcon from "@repo/ui-system/icons/random/mobile/outline.svg";
import SmartBrainIcon from "@repo/ui-system/icons/random/smart-brain/outline.svg";
import StoreIcon from "@repo/ui-system/icons/random/store/outline.svg";
import TargetIcon from "@repo/ui-system/icons/random/target/outline.svg";
import TimeIcon from "@repo/ui-system/icons/random/time/outline.svg";
import TravelIcon from "@repo/ui-system/icons/random/travel/outline.svg";
import UserIcon from "@repo/ui-system/icons/random/user/outline.svg";
import VacuumIcon from "@repo/ui-system/icons/random/vacuum/outline.svg";
import WindIcon from "@repo/ui-system/icons/random/wind/outline.svg";
import StarIcon from "@repo/ui-system/icons/rating/star/outline.svg";
import TrophyIcon from "@repo/ui-system/icons/rating/trophy/outline.svg";

import { type BestQuickPicks } from "@/lib/api/best/quick-picks/schema";

export type Category = {
  id: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type IconRule = {
  keywords: string[];
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

// Order matters — more specific rules must come before broader ones.
const KEYWORD_RULES: IconRule[] = [
  {
    keywords: ["headphones", "earbuds", "audio", "speaker", "sound", "music"],
    icon: HeadphonesIcon,
  },
  {
    keywords: ["vacuum", "robotic", "cleaning", "cleaner"],
    icon: VacuumIcon,
  },
  {
    keywords: [
      "kitchen",
      "mixer",
      "cooking",
      "baking",
      "cookware",
      "appliance",
      "gadget",
    ],
    icon: MixerIcon,
  },
  {
    keywords: ["air", "fan", "purifier", "heater", "cooling"],
    icon: WindIcon,
  },
  {
    keywords: ["coffee", "espresso", "brew", "barista", "latte", "cappuccino"],
    icon: CoffeeIcon,
  },
  {
    keywords: ["mobile", "smartphone", "iphone", "android", "5g"],
    icon: MobileIcon,
  },
  {
    keywords: ["travel", "luggage", "suitcase", "backpack", "flight"],
    icon: TravelIcon,
  },
  {
    keywords: [
      "laptop",
      "computer",
      "tech",
      "electronics",
      "smart",
      "tablet",
      "phone",
    ],
    icon: SmartBrainIcon,
  },
  {
    keywords: ["gaming", "console", "sport", "fitness", "gym"],
    icon: TrophyIcon,
  },
  {
    keywords: ["garden", "lawn", "outdoor", "camping"],
    icon: GlobeIcon,
  },
  {
    keywords: ["camera", "photo", "lens", "tool", "hardware"],
    icon: TargetIcon,
  },
  {
    keywords: ["watch", "clock", "wearable", "time"],
    icon: TimeIcon,
  },
  {
    keywords: ["book", "office", "stationery", "accessory", "accessories"],
    icon: ListIcon,
  },
  {
    keywords: ["personal", "care", "beauty", "health", "grooming"],
    icon: UserIcon,
  },
  {
    keywords: ["shopping", "deals", "store", "retail"],
    icon: StoreIcon,
  },
];

function resolveIcon(
  category: string,
): React.ComponentType<React.SVGProps<SVGSVGElement>> {
  const lower = category.toLowerCase();
  return (
    KEYWORD_RULES.find((r) => r.keywords.some((k) => lower.includes(k)))
      ?.icon ?? StarIcon
  );
}

/** Derive ordered, unique categories from the actual data — no hardcoded list. */
export function getCategoriesFromData(items: BestQuickPicks): Category[] {
  const seen = new Set<string>();
  const result: Category[] = [];
  for (const item of items) {
    if (!seen.has(item.category)) {
      seen.add(item.category);
      result.push({
        id: item.category,
        label: item.category
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
        icon: resolveIcon(item.category),
      });
    }
  }
  return result;
}
